/**
 * Core domain types for an erasure.
 *
 * An erasure is a flat list of `PageElement`s. Keeping the page flat (rather than
 * nesting words inside paragraphs) means every mutation is an O(n) scan at worst and
 * every element has a stable, globally unique id — which is what the gesture layer
 * and the patch-based history both key off.
 */

/** A single character within a word, individually markable via the word editor. */
export interface Character {
  character: string;
  id: string;
  isMarked: boolean;
}

/** A word: the unit a reader marks to erase. */
export interface Word {
  type: "word";
  id: string;
  characters: Character[];
  isMarked: boolean;
  /**
   * True once the reader has marked individual characters within this word, at which
   * point the word renders character-by-character instead of as a single span.
   */
  customCharacters: boolean;
}

/** A paragraph boundary. Rendered as a line break. */
export interface Break {
  type: "break";
  id: string;
}

export type PageElement = Word | Break;

/** Metadata about the source article, as returned by the article extractor. */
export interface ArticleMeta {
  title?: string;
  url?: string;
  author?: string;
  published?: string;
  description?: string;
  image?: string;
  /** The human-readable slug this erasure is stored under. */
  name?: string;
}

/** Reconstructs the plain text of a word. */
export const wordText = (word: Word): string =>
  word.characters.map((c) => c.character).join("");

/** Type guard narrowing a page element to a word. */
export const isWord = (element: PageElement): element is Word => element.type === "word";

/**
 * The text that survives erasure — the poem itself.
 *
 * A word contributes nothing when it is marked. A word with custom characters
 * contributes only its unmarked characters, which is how a reader carves a
 * shorter word out of a longer one.
 */
export const erasureText = (page: PageElement[]): string => {
  const lines: string[] = [];
  let current: string[] = [];

  for (const element of page) {
    if (element.type === "break") {
      lines.push(current.join(" "));
      current = [];
      continue;
    }
    if (element.isMarked) continue;

    const visible = element.customCharacters
      ? element.characters
          .filter((c) => !c.isMarked)
          .map((c) => c.character)
          .join("")
      : wordText(element);

    if (visible.length > 0) current.push(visible);
  }
  lines.push(current.join(" "));

  return lines
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};
